import { useEffect, useState, useRef } from "react";
import api from "../../services/api";
import { useLocation } from "react-router-dom";
import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";

// 1. SEARCH BAR COMPONENT
const SearchBar = ({ value, onChange, placeholder = "Search here..." }) => (
  <div style={styles.searchContainer}>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.searchInput}
    />
  </div>
);

const COLORS = {
  primary: "#0D47A1",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
  danger: "#D32F2F",
  success: "#16A34A",
};

const Customers = () => {
  const [parties, setParties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  // 2. SEARCH STATE
  const [searchTerm, setSearchTerm] = useState("");

  const location = useLocation();
  const fileInputRef = useRef();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    type: "customer",
    balance: ""
  });

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const formRef = useRef(null);
  const dropdownRef = useRef(null);

  const loadParties = async () => {
    try {
      const res = await api.get("/parties/list.php");
      setParties(res.data?.data || []);
    } catch {
      setError("Failed to load parties.");
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/parties/import.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Import successful");
      loadParties();
    } catch (err) {
      alert("Import failed");
    }
  };

  useEffect(() => {
    loadParties();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const f = params.get("filter");
    if (f === "receivable" || f === "payable") setFilter(f);
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showForm && formRef.current && !formRef.current.contains(event.target)) {
        setShowForm(false);
        setEditingId(null);
        setForm({ name: "", phone: "", type: "customer", balance: "" });
        setError("");
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Party name is required"); return; }
    try {
      setLoading(true);
      if (editingId) {
        await api.post("/parties/update.php", { id: editingId, ...form });
      } else {
        await api.post("/parties/create.php", { ...form, balance: parseFloat(form.balance) || 0 });
      }
      setForm({ name: "", phone: "", type: "customer", balance: "" });
      setEditingId(null);
      setShowForm(false);
      loadParties();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (party) => {
    setForm({ name: party.name, phone: party.phone || "", type: party.type || "customer", balance: party.balance || "" });
    setEditingId(party.id);
    setShowForm(true);
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this party?")) return;
    try {
      await api.post("/parties/delete.php", { id });
      loadParties();
    } catch {
      setError("Delete failed. Try again.");
    }
  };

  // 3. FILTER & SEARCH LOGIC
  const filteredParties = parties.filter((p) => {
    const amount = parseFloat(p.balance) || 0;
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         p.phone?.includes(searchTerm);
    
    if (!matchesSearch) return false;

    if (filter === "receivable") return p.type === "customer" && amount > 0;
    if (filter === "payable") return p.type === "supplier" && amount > 0;

    return true;
  });

  const handleExportExcel = () => {
    let url = "https://codesaarthi.online/khatasaarthi/api/parties/export.php";
    if (filter === "payable") url += "?type=payable";
    else if (filter === "receivable") url += "?type=receivable";
    window.open(url, "_blank");
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Parties / Suppliers</h2>
          <p style={styles.subtitle}>Manage parties and suppliers</p>
          
          {/* 4. SEARCH BAR ADDED HERE */}
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search by name or phone..." 
          />
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: 'flex-start' }}>
          <button style={styles.addBtn} onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ name: "", phone: "", type: "customer", balance: "" }); setError(""); }}>
            + Add Party
          </button>
          <button style={{ ...styles.addBtn, background: COLORS.success }} onClick={handleExportExcel}>Export Excel</button>
          <button style={{ ...styles.addBtn, background: "#7B1FA2" }} onClick={() => fileInputRef.current.click()}>Import Excel</button>
          <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: "none" }} onChange={handleImportExcel} />
        </div>
      </div>

      <div style={styles.filterWrap}>
        <button style={{ ...styles.filterBtn, ...(filter === "all" ? styles.activeFilter : {}) }} onClick={() => setFilter("all")}>All</button>
        <button style={{ ...styles.filterBtn, ...(filter === "receivable" ? styles.activeFilter : {}) }} onClick={() => setFilter("receivable")}>Receivable</button>
        <button style={{ ...styles.filterBtn, ...(filter === "payable" ? styles.activeFilter : {}) }} onClick={() => setFilter("payable")}>Payable</button>
      </div>

      {showForm && (
        <div ref={formRef} style={styles.formCard}>
          <form onSubmit={submit} style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label}>Party Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} placeholder="Enter party name" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone Number</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={styles.input} placeholder="Enter 10 digit phone" maxLength={10} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Type</label>
              <div style={styles.dropdown} ref={dropdownRef}>
                <div style={styles.dropdownHeader} onClick={() => setOpen(!open)}>
                  {form.type === "customer" ? "Customer" : "Supplier"}
                  <span style={{ ...styles.arrow, transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
                </div>
                {open && (
                  <div style={styles.dropdownMenu}>
                    <div style={styles.dropdownItem} onClick={() => { setForm({ ...form, type: "customer" }); setOpen(false); }}>Customer</div>
                    <div style={styles.dropdownItem} onClick={() => { setForm({ ...form, type: "supplier" }); setOpen(false); }}>Supplier</div>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Opening Balance</label>
              <input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} style={styles.input} placeholder="Enter amount" />
            </div>
            <div style={styles.buttonWrap}>
              <button type="submit" style={styles.saveBtn} disabled={loading}>{loading ? "Saving..." : editingId ? "Update" : "Save"}</button>
            </div>
          </form>
          {error && <p style={styles.error}>{error}</p>}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Party Name</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredParties.length === 0 ? (
                <tr><td colSpan="6" style={styles.empty}>No parties found</td></tr>
              ) : (
                filteredParties.map((p, index) => {
                  const amount = parseFloat(p.balance) || 0;
                  return (
                    <tr key={p.id} style={p.type === "supplier" && amount > 0 ? styles.payableRow : {}}>
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.tdBold}>{p.name}</td>
                      <td style={styles.td}>{p.phone || "-"}</td>
                      <td style={styles.td}>{p.type === "supplier" ? "Supplier" : "Customer"}</td>
                      <td style={styles.td}>
                        ₹ {amount.toLocaleString("en-IN")}
                        {amount > 0 && (
                          <span style={{ color: p.type === "supplier" ? COLORS.danger : COLORS.success, fontWeight: 600, marginLeft: 6 }}>
                            ({p.type === "supplier" ? "Payable" : "Receivable"})
                          </span>
                        )}
                      </td>
                      <td style={styles.actionCell}>
                        <button style={styles.iconBtn} onClick={() => handleEdit(p)}><img src={editIcon} alt="edit" style={styles.icon} /></button>
                        <button style={styles.iconBtn} onClick={() => handleDelete(p.id)}><img src={deleteIcon} alt="delete" style={styles.icon} /></button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles = {
  // New Search Styles
  searchContainer: { marginTop: "12px", width: "300px" },
  searchInput: { 
    width: "100%", 
    padding: "8px 12px", 
    borderRadius: "8px", 
    border: `1px solid ${COLORS.border}`, 
    fontSize: "14px", 
    outline: "none" 
  },

  page: { background: COLORS.bg, padding: "10px clamp(15px,4vw,30px) 30px", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 25 },
  title: { fontSize: 24, fontWeight: 700, color: COLORS.text },
  subtitle: { fontSize: 14, color: COLORS.muted, marginTop: 6 },
  addBtn: { background: COLORS.primary, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, fontWeight: 600, cursor: "pointer" },
  formCard: { background: COLORS.card, padding: 20, borderRadius: 14, marginBottom: 25, border: `1px solid ${COLORS.border}` },
  formRow: { display: "grid", gridTemplateColumns: "1fr", gap: 14 },
  field: { display: "flex", flexDirection: "column" },
  label: { fontSize: 13, marginBottom: 6, color: COLORS.muted },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" },
  dropdown: { position: "relative", width: "100%" },
  dropdownHeader: { border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 14px", background: "#fff", display: "flex", justifyContent: "space-between", cursor: "pointer" },
  arrow: { fontSize: 14 },
  dropdownMenu: { position: "absolute", top: "110%", left: 0, width: "100%", background: "#fff", borderRadius: 12, border: `1px solid ${COLORS.border}`, zIndex: 100 },
  dropdownItem: { padding: "12px 14px", cursor: "pointer" },
  buttonWrap: { display: "flex", width: "100%" },
  saveBtn: { background: COLORS.primary, color: "#fff", border: "none", padding: "12px", borderRadius: 8, fontWeight: 600, cursor: "pointer", width: "100%" },
  error: { marginTop: 10, color: COLORS.danger, fontSize: 13 },
  card: { background: COLORS.card, borderRadius: 16, padding: 20 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 700 },
  th: { textAlign: "left", padding: "14px 8px", fontSize: 13, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  thCenter: { textAlign: "center", padding: "14px 8px", fontSize: 13, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "14px 8px", borderBottom: `1px solid ${COLORS.border}` },
  tdBold: { padding: "14px 8px", fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` },
  actionCell: { padding: "14px 8px", display: "flex", justifyContent: "center", gap: 14 },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer" },
  icon: { width: 18, height: 18 },
  empty: { textAlign: "center", padding: 25, color: COLORS.muted },
  filterWrap: { display: "flex", gap: 10, marginBottom: 15, flexWrap: "wrap" },
  filterBtn: { padding: "8px 14px", borderRadius: 20, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer", fontSize: 13 },
  activeFilter: { background: COLORS.primary, color: "#fff", border: "none" },
  payableRow: { color: COLORS.danger, background: "#FFEBEE" },
};

export default Customers;